import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface RecentOutOfStockRow {
  product_id: string;
  product_name: string;
  sku: string | null;
  category_id: string | null;
  category_name: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  location_id: string;
  location_name: string;
  last_stockout_at: string | null;
  current_quantity_on_hand: number;
  current_quantity_foh: number;
  current_quantity_boh: number;
  items_sold: number;
  last_sale_at: string | null;
  in_transit: number;
  on_order: number;
}

export interface RecentOutOfStockFilters {
  locationId?: string;
  vendorId?: string;
  categoryId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useRecentOutOfStockReport(
  filters: RecentOutOfStockFilters = {},
  options: { enabled?: boolean } = {}
) {
  const { organization } = useAuth();
  const organizationId = organization?.id;

  const {
    data: rows,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['recent-out-of-stock', organizationId, filters],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase.rpc('get_recent_out_of_stock_report', {
        p_organization_id: organizationId,
        p_location_id: filters.locationId || null,
        p_date_from: filters.dateFrom || null,
        p_date_to: filters.dateTo || null,
        p_vendor_id: filters.vendorId || null,
        p_category_id: filters.categoryId || null,
      });

      if (error) throw error;
      return (data || []) as RecentOutOfStockRow[];
    },
    enabled: !!organizationId && (options.enabled ?? true),
  });

  const summary = {
    totalRows: (rows || []).length,
    totalItemsSold: (rows || []).reduce((sum, r) => sum + (r.items_sold || 0), 0),
    totalInTransit: (rows || []).reduce((sum, r) => sum + (r.in_transit || 0), 0),
    totalOnOrder: (rows || []).reduce((sum, r) => sum + (r.on_order || 0), 0),
  };

  return {
    rows: rows || [],
    isLoading,
    error,
    refetch,
    summary,
  };
}

